import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo } from '../data/companyData';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [sbuDropdownOpen, setSbuDropdownOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const solid = !isHome || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about', hasDropdown: true },
    { name: 'SBUs', path: '/sbus', hasDropdown: true },
    { name: 'Contact', path: '/contact' }
  ];

  const aboutDropdownItems = [
    { name: 'Who We Are', path: '/who-we-are' },
    { name: 'Mission & Vision', path: '/mission-vision' },
    { name: 'Partnerships', path: '/partnerships' },
    { name: 'Leaders & Team', path: '/leaders-team' }
  ];

  const sbuDropdownItems = [
    { name: 'How Our Operations Are Organised', path: '/operations-organization' },
    { name: 'Agronomy', path: '/agronomy' },
    { name: 'Seed Production & Irrigation Services', path: '/seed-production-irrigation' },
    { name: 'Commercial Farming', path: '/commercial-farming' },
    { name: 'Value Addition', path: '/value-addition' },
    { name: 'Trading', path: '/trading' }
  ];

  const operationsDropdownItems = [
    { name: 'Agronomy', path: '/agronomy' },
    { name: 'Value Addition', path: '/value-addition' }
  ];

  const agronomyDropdownItems = [
    { name: 'Seed Production & Irrigation Services', path: '/seed-production-irrigation' },
    { name: 'Commercial Farming', path: '/commercial-farming' }
  ];

  return (
    <nav className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
      solid
        ? "bg-gray-50/80 backdrop-blur-xl border-b border-gray-200/60 shadow-lg"
        : "bg-transparent border-b border-white/20"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div 
            onClick={() => window.location.href = '/'}
            className="flex-shrink-0 cursor-pointer"
          >
            <img 
              src={companyInfo.logo} 
              alt={companyInfo.name} 
              className="h-16 w-16"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8 ml-auto">
            {navItems.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div className="relative group">
                    <button
                      className={`text-gray-700/80 hover:text-green-600 px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center ${
                        solid ? "text-gray-700/80 hover:text-green-600" : "text-white/85 hover:text-white"
                      } ${location.pathname.startsWith(item.path) ? 'text-green-600' : ''}`}
                    >
                      {item.name}
                      <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7 7" />
                      </svg>
                    </button>
                    
                    {/* Dropdown Menu */}
                    <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      {item.name === 'About' && (
                        <>
                          {aboutDropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.path}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors"
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </>
                      )}
                      {item.name === 'SBUs' && (
                        <>
                          {sbuDropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.path}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors"
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`text-gray-700/80 hover:text-green-600 px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                      solid ? "text-gray-700/80 hover:text-green-600" : "text-white/85 hover:text-white"
                    } ${location.pathname === item.path ? 'text-green-600' : ''}`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`flex h-11 w-11 items-center justify-center rounded-full border ${
              solid
                ? "border-gray-300 bg-gray-50 text-gray-700"
                : "border-white/20 bg-white/10 text-white backdrop-blur-md"
            }`}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-50 border-t border-gray-200">
            <div className="px-2 py-2 space-y-1">
              {navItems.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => {
                          if (item.name === 'About') setAboutDropdownOpen(!aboutDropdownOpen);
                          if (item.name === 'SBUs') setSbuDropdownOpen(!sbuDropdownOpen);
                        }}
                        className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700/80 hover:bg-green-50 hover:text-green-600"
                      >
                        {item.name}
                      </button>
                      {item.name === 'About' && aboutDropdownOpen && (
                        <div className="pl-4 py-2 space-y-1">
                          {aboutDropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.path}
                              className="block px-3 py-2 text-sm text-gray-600 hover:text-green-600"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                      {item.name === 'SBUs' && sbuDropdownOpen && (
                        <div className="pl-4 py-2 space-y-1">
                          {sbuDropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.path}
                              className="block px-3 py-2 text-sm text-gray-600 hover:text-green-600"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className="block px-3 py-2 text-sm font-medium text-gray-700/80 hover:bg-green-50 hover:text-green-600"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
